import React, { useRef, useEffect, useState } from 'react';
import './RemoteServer.css';

// Declare window interface for runtime config
declare global {
  interface Window {
    REACT_APP_NOVNC_URL?: string;
  }
}

const RemoteServer: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clipboardText, setClipboardText] = useState('');
  const [showClipboardHelper, setShowClipboardHelper] = useState(false);
  const [isOpeningBrowser, setIsOpeningBrowser] = useState(false);

   // Get base URL from window object (runtime) or environment variable (build time) or use localhost as fallback
  const baseUrl = 'https://vnc.cebugrandrealty.com';
  const vncpassword = 'C82jyE29GgJ6';

  // VNC URL with your specified parameters
  const vncUrl = `${baseUrl}/vnc.html?password=${vncpassword}&resize=off&view_only=false&view_clip=true&shared=true&autoconnect=true&reconnect=true&reconnect_delay=2000`;


  useEffect(() => {
    // Check if the VNC server is accessible
    const checkConnection = async () => {
      try {
        // Try to fetch the VNC page
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        await fetch(`${baseUrl}/`, {
          mode: 'no-cors',
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        setIsConnected(true);
        setError(null);
      } catch (err) {
        setIsConnected(false);
        setError(`Unable to connect to VNC server. Please ensure it is running on ${baseUrl}`);
      } finally {
        setIsLoading(false);
      }
    };

    checkConnection();

    // Set up periodic connection checks
    const interval = setInterval(checkConnection, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [baseUrl]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setIsConnected(true);
  };

  const handleReconnect = () => {
    setIsLoading(true);
    setError(null);
    // Force iframe reload
    if (iframeRef.current) {
      iframeRef.current.src = vncUrl;
    }
  };

  // Read from system clipboard and set to textarea
  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setClipboardText(text);
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      alert('Unable to read clipboard. Please paste manually into the text area.');
    }
  };

  // Send clipboard text to VNC as keyboard input
  const handleSendToVNC = () => {
    if (!clipboardText || !iframeRef.current?.contentWindow) return;
    
    try {
      // Try to send via postMessage to noVNC
      iframeRef.current.contentWindow.postMessage({
        type: 'clipboard',
        text: clipboardText
      }, baseUrl);
      
      alert('Text sent! Use Ctrl+V (or Cmd+V) inside the VNC session to paste.');
    } catch (err) {
      console.error('Failed to send to VNC:', err);
    }
  };

  // Open Chrome Browser on remote server
  const handleOpenChromeBrowser = async () => {
    setIsOpeningBrowser(true);
    try {
      const response = await fetch('https://auto.cebugrandrealty.com/api/v2/open-browser', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        alert('Chrome browser opened successfully on remote server!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to open Chrome browser: ${errorData.message || response.statusText}`);
      }
    } catch (err) {
      console.error('Failed to open Chrome browser:', err);
      alert('Failed to open Chrome browser. Please check the connection.');
    } finally {
      setIsOpeningBrowser(false);
    }
  };

  // Chrome Icon Component
  const ChromeIcon = () => (
    <svg 
      className="chrome-icon" 
      viewBox="0 0 48 48" 
      width="18" 
      height="18"
    >
      {/* Red section */}
      <path 
        fill="#EA4335" 
        d="M24 4C12.95 4 4 12.95 4 24c0 3.85 1.09 7.44 2.97 10.49l9.91-17.18A11.94 11.94 0 0 1 24 12.03h19.73C40.42 7.07 32.69 4 24 4z"
      />
      {/* Yellow section */}
      <path 
        fill="#FBBC04" 
        d="M43.73 12.03H24c4.12 0 7.73 2.08 9.87 5.25l.01.02 9.91 17.18A19.88 19.88 0 0 0 44 24c0-4.41-1.43-8.48-3.85-11.78l3.58-.19z"
      />
      {/* Green section */}
      <path 
        fill="#34A853" 
        d="M6.97 34.49A19.94 19.94 0 0 0 24 44c8.69 0 16.42-4.07 19.73-12.03L33.82 17.3a11.94 11.94 0 0 1-9.82 18.67c-4.12 0-7.73-2.08-9.87-5.25l-7.16 3.77z"
      />
      {/* Blue center circle */}
      <circle fill="#4285F4" cx="24" cy="24" r="8"/>
      {/* White inner circle */}
      <circle fill="#FFFFFF" cx="24" cy="24" r="4"/>
    </svg>
  );

  return (
    <div className="remote-server-container">
      <div className="remote-server-header">
        <h1>Remote Server</h1>
        <div className="connection-status">
          <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}></span>
          <span className="status-text">
            {isLoading ? 'Connecting...' : isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {!isConnected && !isLoading && (
            <button className="reconnect-btn" onClick={handleReconnect}>
              Reconnect
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={handleReconnect}>Try Again</button>
        </div>
      )}

      {/* Clipboard Helper Section */}
      <div className="clipboard-helper-section">
        <div className="clipboard-helper-buttons">
          <button 
            className="clipboard-toggle-btn"
            onClick={() => setShowClipboardHelper(!showClipboardHelper)}
          >
            {showClipboardHelper ? '✕ Hide Clipboard Helper' : '📋 Show Clipboard Helper'}
          </button>
          
          <button 
            className="open-chrome-btn"
            onClick={handleOpenChromeBrowser}
            disabled={isOpeningBrowser}
          >
            {isOpeningBrowser ? (
              <>
                <span className="spinner-small"></span>
                Opening...
              </>
            ) : (
              <>
                <ChromeIcon />
                Open Chrome Browser
              </>
            )}
          </button>
        </div>
        
        {showClipboardHelper && (
          <div className="clipboard-helper">
            <p className="clipboard-help-text">
              <strong>Tip:</strong> Browser security prevents direct copy/paste into VNC. 
              Use this helper to transfer clipboard content.
            </p>
            <div className="clipboard-controls">
              <textarea
                value={clipboardText}
                onChange={(e) => setClipboardText(e.target.value)}
                placeholder="Paste your text here (Ctrl+V / Cmd+V), or click 'Read Clipboard'"
                rows={3}
              />
              <div className="clipboard-buttons">
                <button onClick={handlePasteFromClipboard} className="clipboard-btn">
                  📥 Read Clipboard
                </button>
                <button 
                  onClick={handleSendToVNC} 
                  className="clipboard-btn send-btn"
                  disabled={!clipboardText}
                >
                  📤 Send to VNC
                </button>
                <button 
                  onClick={() => setClipboardText('')} 
                  className="clipboard-btn clear-btn"
                >
                  🗑️ Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="vnc-container">
        {isLoading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
            <p>Connecting to remote server...</p>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={vncUrl}
          className="vnc-frame"
          onLoad={handleIframeLoad}
          title="Remote Server VNC"
          allow="fullscreen; clipboard-read; clipboard-write"
        />
      </div>

      <div className="remote-server-footer">
        <div className="info-section">
          <p><strong>Server:</strong> {baseUrl}</p>
          <p><strong>Settings:</strong> View clip enabled, Shared session, Auto-reconnect</p>
        </div>
        <div className="controls-section">
          <button
            className="fullscreen-btn"
            onClick={() => {
              if (iframeRef.current) {
                iframeRef.current.requestFullscreen();
              }
            }}
          >
            Fullscreen
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoteServer;
